import { useToasts } from "react-toast-notifications";

import { DELETE_DOCUMENTS, FETCH_DOCUMENTS } from "@gql/documents";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type DeleteDocument,
  type DeleteDocumentVariables,
  type Documents,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

type Doc = Documents["me"]["documents"]["data"][number];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  readonly document: Doc;
  readonly onSuccess: () => void;
}

export const DeleteDocumentModal = ({
  isOpen,
  onClose,
  document,
  onSuccess,
}: Props) => {
  const [deleteDocumentMutation, { loading }] = useNorthbeamMutation<
    DeleteDocument,
    DeleteDocumentVariables
  >(DELETE_DOCUMENTS, {
    refetchQueries: [{ query: FETCH_DOCUMENTS }],
  });

  const { addToast } = useToasts();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={<div className="font-bold text-xl">Delete Document</div>}
    >
      <div className="mb-2">
        <div className="font-semibold mb-1">Document:</div>
        <div>{document.name}</div>
      </div>
      <div className="flex flex-row w-full gap-2 align-top">
        <div className="pt-1">
          <i className="fa-solid fa-exclamation-triangle text-sun+2" />
        </div>
        <div>
          Deleting this document will mean it will no longer be available to
          you.{" "}
          <span className="font-semibold">
            This is not a recoverable action.
          </span>
        </div>
      </div>
      <PrimaryButton
        loading={loading}
        className="font-bold bg-mars hover:bg-mars+1 flex items-center gap-2"
        onClick={async () => {
          await deleteDocumentMutation({
            variables: {
              documentName: document.name,
            },
          });
          addToast(`The document, ${document.name}, has been deleted`, {
            appearance: "success",
            autoDismiss: true,
          });
          onSuccess();
        }}
      >
        <i className="fa-regular fa-sharp fa-trash-alt text-sm" />
        DELETE
      </PrimaryButton>
    </Modal>
  );
};
